import type { Resources } from "../../tooling/i18n/i18next";
import type { Checkout, CheckoutStep } from "./checkout.schemas";

export const CHECKOUT_STEP_NAMES_BASE = {
	diagnose: "/checkout/diagnose",
} as const;

export const CHECKOUT_STEP_NAMES = {
	prescriptionChoice: "/checkout/prescription-choice",
	prescriptionOrder: "/checkout/prescription-order",
	diagnoseSymptoms: `${CHECKOUT_STEP_NAMES_BASE.diagnose}/symptoms`,
	diagnoseTreatment: `${CHECKOUT_STEP_NAMES_BASE.diagnose}/treatment`,
	diagnoseCannabisTreatment: `${CHECKOUT_STEP_NAMES_BASE.diagnose}/cannabis-treatment`,
	diagnoseTherapyEffects: `${CHECKOUT_STEP_NAMES_BASE.diagnose}/therapy-effects`,
	diagnoseExclusionCriteria: `${CHECKOUT_STEP_NAMES_BASE.diagnose}/exclusion-criteria`,
	diagnoseDifferentMedication: `${CHECKOUT_STEP_NAMES_BASE.diagnose}/different-medication`,
	awareness: "/checkout/awareness",
	crossSell: "/checkout/cross-sell",
	delivery: "/checkout/delivery",
	summary: "/checkout/summary",
} as const;

export const checkoutSteps = [
	CHECKOUT_STEP_NAMES.prescriptionOrder,
	CHECKOUT_STEP_NAMES.diagnoseSymptoms,
	CHECKOUT_STEP_NAMES.diagnoseTreatment,
	CHECKOUT_STEP_NAMES.diagnoseCannabisTreatment,
	CHECKOUT_STEP_NAMES.diagnoseTherapyEffects,
	CHECKOUT_STEP_NAMES.diagnoseExclusionCriteria,
	CHECKOUT_STEP_NAMES.diagnoseDifferentMedication,
	CHECKOUT_STEP_NAMES.awareness,
	CHECKOUT_STEP_NAMES.crossSell,
	CHECKOUT_STEP_NAMES.summary,
	// Placed last to avoid interfering with sequential step validation.
	// This is a separate flow (I already have a prescription -> upload)
	CHECKOUT_STEP_NAMES.prescriptionChoice,
	// Also placed after summary, for the same reason: the delivery step is entered
	// only for baskets spanning more than one vendor, so making it summary's linear
	// predecessor would block single-vendor checkout from ever reaching summary.
	CHECKOUT_STEP_NAMES.delivery,
] as const;

export const checkoutStepsByType = {
	normal: checkoutSteps,
} as const;

export const checkoutStepToSchema: Record<
	CheckoutStep,
	keyof Omit<Checkout, "expireAt">
> = {
	[CHECKOUT_STEP_NAMES.prescriptionChoice]: "prescription",
	[CHECKOUT_STEP_NAMES.prescriptionOrder]: "prescription",
	[CHECKOUT_STEP_NAMES.diagnoseSymptoms]: "diagnose",
	[CHECKOUT_STEP_NAMES.diagnoseTreatment]: "diagnose",
	[CHECKOUT_STEP_NAMES.diagnoseCannabisTreatment]: "diagnose",
	[CHECKOUT_STEP_NAMES.diagnoseTherapyEffects]: "diagnose",
	[CHECKOUT_STEP_NAMES.diagnoseExclusionCriteria]: "diagnose",
	[CHECKOUT_STEP_NAMES.diagnoseDifferentMedication]: "diagnose",
	[CHECKOUT_STEP_NAMES.awareness]: "awareness",
	[CHECKOUT_STEP_NAMES.crossSell]: "crossSell",
	[CHECKOUT_STEP_NAMES.delivery]: "delivery",
	[CHECKOUT_STEP_NAMES.summary]: "summary",
} as const;

export interface BackButtonConfig {
	pathname: string;
	labelKey: keyof Resources["checkout"];
}

export const checkoutNavigation: Record<
	CheckoutStep,
	BackButtonConfig | undefined
> = {
	[CHECKOUT_STEP_NAMES.prescriptionOrder]: {
		pathname: "/products",
		labelKey: "backToCart",
	},
	[CHECKOUT_STEP_NAMES.prescriptionChoice]: {
		pathname: CHECKOUT_STEP_NAMES.prescriptionOrder,
		labelKey: "steps.prescription",
	},
	[CHECKOUT_STEP_NAMES.diagnoseSymptoms]: {
		pathname: CHECKOUT_STEP_NAMES.prescriptionOrder,
		labelKey: "steps.prescription",
	},
	[CHECKOUT_STEP_NAMES.diagnoseCannabisTreatment]: {
		pathname: CHECKOUT_STEP_NAMES.prescriptionOrder,
		labelKey: "steps.prescription",
	},
	[CHECKOUT_STEP_NAMES.diagnoseTreatment]: {
		pathname: CHECKOUT_STEP_NAMES.prescriptionOrder,
		labelKey: "steps.prescription",
	},
	[CHECKOUT_STEP_NAMES.diagnoseTherapyEffects]: {
		pathname: CHECKOUT_STEP_NAMES.prescriptionOrder,
		labelKey: "steps.prescription",
	},
	[CHECKOUT_STEP_NAMES.diagnoseExclusionCriteria]: {
		pathname: CHECKOUT_STEP_NAMES.prescriptionOrder,
		labelKey: "steps.prescription",
	},
	[CHECKOUT_STEP_NAMES.diagnoseDifferentMedication]: {
		pathname: CHECKOUT_STEP_NAMES.prescriptionOrder,
		labelKey: "steps.prescription",
	},
	[CHECKOUT_STEP_NAMES.awareness]: {
		pathname: CHECKOUT_STEP_NAMES_BASE.diagnose,
		labelKey: "steps.diagnose",
	},
	[CHECKOUT_STEP_NAMES.crossSell]: {
		pathname: CHECKOUT_STEP_NAMES.awareness,
		labelKey: "steps.awareness",
	},
	[CHECKOUT_STEP_NAMES.delivery]: {
		pathname: CHECKOUT_STEP_NAMES.crossSell,
		labelKey: "steps.crossSell",
	},
	[CHECKOUT_STEP_NAMES.summary]: {
		pathname: CHECKOUT_STEP_NAMES.delivery,
		labelKey: "steps.delivery",
	},
};

export const checkoutStepDiagnoseSteps = [
	"symptoms",
	"treatment",
	"cannabis-treatment",
	"therapy-effects",
	"exclusion-criteria",
	"different-medication",
] as const;

export const checkoutDiagnoseStepsByPrescriptionOrderType = {
	initial: [
		"symptoms",
		"treatment",
		"cannabis-treatment",
		"exclusion-criteria",
		"different-medication",
	],

	"follow-up": [
		"therapy-effects",
		"exclusion-criteria",
		"different-medication",
	],
} as const;

export const checkoutDiagnoseStepToSchema = {
	symptoms: "symptoms",
	treatment: "treatment",
	"cannabis-treatment": "cannabisTreatment",
	"therapy-effects": "therapyEffects",
	"exclusion-criteria": "hasExclusions",
	"different-medication": "differentMedication",
} as const;

export const checkoutDiagnoseExclusionCriterias = [
	"used_cannabis_too_fast",
	"disadvantage",
	"psychological_diseases",
	"physical_diseases",
	"severe_depression",
	"chronic_illness",
	"not_driver",
	"allergic",
] as const;

export const checkoutDiagnoseComplaints = [
	"anti_aging",
	"sleep_disorders",
	"chronic_pain",
	"stress",
	"migraine",
	"depression",
	"adhd",
	"spactic",
	"tinnitus",
	"epilepsy",
	"nausea_after_chemotherapy",
	"loss_of_appetite",
	"anxiety_disorder",
	"tourette_syndrome",
	"rheumatoid_arthritis",
	"dementia",
	"glaucoma",
	"intestinal_disease",
	"neurodermatitis",
	"psoriasis",
	"acne_inversa",
	"hyper_hidrosis",
	"arthritis",
	"colitis_ulcerosa",
	"crohns_disease",
	"post_traumatic_stress_disorder",
	"asthma",
	"bladder_spasms",
	"borreliosis",
	"fibromyalgia",
	//"enter_other_disease",
] as const;

export const checkoutDiagnoseComplaintsTop: (typeof checkoutDiagnoseComplaints)[number][] =
	[
		"adhd",
		"anxiety_disorder",
		"chronic_pain",
		"depression",
		"epilepsy",
		"migraine",
		"spactic",
		"tinnitus",
		"nausea_after_chemotherapy",
		"sleep_disorders",
		"dementia",
		"loss_of_appetite",
		"tourette_syndrome",
	];

export const checkoutDiagnoseDifferentMedications = [
	"blood_pressure",
	"anticoagulant",
	"ed_medication",
	"sedative",
	"anti",
	"depressant",
] as const;

export const headerStepperData = [
	{
		labelKey: "steps.prescription",
		pathname: CHECKOUT_STEP_NAMES.prescriptionOrder,
	},
	{ labelKey: "steps.diagnose", pathname: CHECKOUT_STEP_NAMES_BASE.diagnose },
	{ labelKey: "steps.awareness", pathname: CHECKOUT_STEP_NAMES.awareness },
] as const;
